import { useState } from 'react';

import { useCreateInvitation } from '../../hooks/useUserManagement';

interface InviteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function InviteUserModal({ isOpen, onClose }: InviteUserModalProps) {
  const [email, setEmail] = useState('');
  const [activationCode, setActivationCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const createInvitation = useCreateInvitation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      const result = await createInvitation.mutateAsync(email);
      setActivationCode(result.activationCode);
      setEmail('');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to create invitation'
      );
    }
  };

  const handleCopyCode = async () => {
    if (activationCode) {
      await navigator.clipboard.writeText(activationCode);
      setCopyFeedback('Activation code copied to clipboard!');
      setTimeout(() => setCopyFeedback(null), 3000);
    }
  };

  const handleCopyLink = async () => {
    if (activationCode) {
      const link = `${window.location.origin}/activate?code=${activationCode}`;
      await navigator.clipboard.writeText(link);
      setCopyFeedback('Activation link copied to clipboard!');
      setTimeout(() => setCopyFeedback(null), 3000);
    }
  };

  const handleClose = () => {
    setEmail('');
    setActivationCode(null);
    setError(null);
    setCopyFeedback(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-text-dark">
            {activationCode ? 'Invitation Created' : 'Invite User'}
          </h2>
          <button
            onClick={handleClose}
            className="text-text-secondary hover:text-text-dark"
          >
            ✕
          </button>
        </div>

        {!activationCode ? (
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label
                htmlFor="email"
                className="block text-sm font-medium text-text-dark mb-2"
              >
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-3 py-2 border border-border-light rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="user@example.com"
                disabled={createInvitation.isPending}
              />
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={createInvitation.isPending || !email}
                className="btn-primary flex-1"
              >
                {createInvitation.isPending
                  ? 'Creating...'
                  : 'Create Invitation'}
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div>
            <div className="mb-6">
              <p className="text-sm text-text-secondary mb-4">
                Share this activation code with the user:
              </p>
              <div className="bg-gray-50 border border-border-light rounded-md p-4 mb-4">
                <code className="text-xl font-mono font-bold text-primary block text-center">
                  {activationCode}
                </code>
              </div>
              <div className="flex gap-2 mb-4">
                <button
                  onClick={handleCopyCode}
                  className="btn-secondary flex-1"
                >
                  📋 Copy Code
                </button>
                <button
                  onClick={handleCopyLink}
                  className="btn-secondary flex-1"
                >
                  🔗 Copy Link
                </button>
              </div>
              {copyFeedback && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md text-green-700 text-sm">
                  ✓ {copyFeedback}
                </div>
              )}
              <div className="text-sm text-text-secondary space-y-2">
                <p>📝 Instructions for user:</p>
                <ol className="list-decimal list-inside space-y-1 ml-2">
                  <li>Visit the activation page</li>
                  <li>Enter their email and this code</li>
                  <li>Create a passkey to secure their account</li>
                </ol>
              </div>
            </div>
            <button onClick={handleClose} className="btn-primary w-full">
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
